import { useId, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, BOOKS_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { RowActions } from "@/components/RowActions";
import { RecurringTemplateDialog } from "@/components/RecurringTemplateDialog";
import { formatMoney } from "@/lib/format";
import { useReferenceData } from "@/hooks/useReferenceData";
import { useRecurring } from "@/hooks/useRecurring";
import type { RecurringTemplate } from "@/api/types";
import "./BooksRecurring.css";

const FREQUENCY_LABELS: Record<RecurringTemplate["frequency"], string> = {
  weekly: "Weekly",
  biweekly: "Biweekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

/**
 * Books' Recurring tab (ef-042-6, `/books/recurring`) — list, create/edit, an Active
 * toggle and "Post now" against `ef-042-4`'s `/api/demo/books/recurring/*`. A standalone
 * top-level page, same pattern as `BooksVendors.tsx`; this job's own file scope never
 * lists `Books.tsx`.
 *
 * "POST NOW" IS THE ROW'S ONE PRIMARY ACTION (ADR 0009 §4 — the build brief's own words).
 * Edit sits behind `RowActions`' "More actions" disclosure, same split `BooksVendors.tsx`
 * draws between Edit-on-the-row and Delete-behind-the-menu — here it's Post-now-on-the-
 * row and Edit-behind-the-menu, because posting is the thing a farmer does often and
 * editing is rare. The Active switch is a STATE CONTROL, not a command, so it sits next
 * to Post now without competing with it as a second "action" in the ADR's sense.
 */
export function BooksRecurring() {
  const { accounts, entityId } = useReferenceData();
  const { templates, status, error, create, update, postNow } = useRecurring();

  const categoryAccounts = useMemo(() => accounts.filter((a) => a.kind !== "Asset"), [accounts]);
  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a] as const)), [accounts]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RecurringTemplate | null>(null);
  const [postingId, setPostingId] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const listHeadingId = useId();

  function openCreate(): void {
    setEditingTemplate(null);
    setDialogOpen(true);
  }

  function openEdit(template: RecurringTemplate): void {
    setEditingTemplate(template);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
  }

  async function handlePostNow(template: RecurringTemplate): Promise<void> {
    setPostingId(template.id);
    setPostError(null);
    try {
      await postNow(template.id);
    } catch (err) {
      setPostError(
        err instanceof Error ? err.message : "Could not post this template.",
      );
    } finally {
      setPostingId(null);
    }
  }

  async function handleToggleActive(template: RecurringTemplate): Promise<void> {
    setTogglingId(template.id);
    setToggleError(null);
    try {
      await update(template.id, { isActive: !template.isActive });
    } catch (err) {
      setToggleError(
        err instanceof Error ? err.message : "Could not change this template's active state.",
      );
    } finally {
      setTogglingId(null);
    }
  }

  const count = templates?.length ?? 0;

  return (
    <main className="books-recurring-page">
      <PageHeader
        title="Recurring"
        subtitle="Standing bills that post themselves — create a template, then press Post now to catch one up."
        action={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New template
          </button>
        }
      />

      <AreaTabs area="/books" tabs={BOOKS_TABS} label="Books" />

      {/* Ground truth for a tester, rules/ui-breadcrumbs.md §1 — read off the DOM rather
          than inferred from sentence text. */}
      <section
        className="books-recurring"
        aria-labelledby={listHeadingId}
        data-state={status === "ready" && count === 0 ? "empty" : status}
      >
        <h2 id={listHeadingId} className="books-recurring__heading">
          Your templates
        </h2>

        {status === "loading" && <StateMessage tone="loading" title="Loading templates…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load templates." detail={error ?? undefined} />
        )}

        {status === "ready" && count === 0 && (
          <StateMessage
            tone="empty"
            title="No recurring templates yet."
            detail="Add a standing bill — rent, a loan payment, insurance — and Post now will fire it through the same write path as a manual Check or Deposit."
          />
        )}

        {postError && (
          <p className="books-recurring__list-error" role="alert">
            {postError}
          </p>
        )}
        {toggleError && (
          <p className="books-recurring__list-error" role="alert">
            {toggleError}
          </p>
        )}

        {status === "ready" && templates !== null && count > 0 && (
          <ul className="books-recurring__list" aria-labelledby={listHeadingId}>
            {templates.map((template) => {
              const category = accountById.get(template.accountId);
              const switchId = `recurring-active-${template.id}`;
              return (
                <li
                  key={template.id}
                  className="books-recurring__row"
                  data-recurring-id={template.id}
                >
                  <span className="books-recurring__meta">
                    <span className="books-recurring__category">
                      {category?.name ?? "Uncategorized"}
                    </span>
                    <span className="books-recurring__sub">
                      {FREQUENCY_LABELS[template.frequency]} · Next due {template.nextDueOn}
                    </span>
                  </span>

                  <span className="books-recurring__amount">{formatMoney(template.amount)}</span>

                  <span className="books-recurring__active">
                    <label htmlFor={switchId}>Active</label>
                    <input
                      id={switchId}
                      type="checkbox"
                      role="switch"
                      checked={template.isActive}
                      disabled={togglingId === template.id}
                      onChange={() => void handleToggleActive(template)}
                    />
                  </span>

                  <span className="books-recurring__row-actions">
                    <button
                      type="button"
                      className="btn btn--primary books-recurring__post-now"
                      disabled={postingId === template.id}
                      onClick={() => void handlePostNow(template)}
                    >
                      {postingId === template.id ? "Posting…" : "Post now"}
                    </button>
                    <RowActions label="More actions">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => openEdit(template)}
                      >
                        Edit
                      </button>
                    </RowActions>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {dialogOpen && (
        <RecurringTemplateDialog
          entityId={entityId}
          categoryAccounts={categoryAccounts}
          template={editingTemplate}
          onClose={closeDialog}
          onCreate={create}
          onUpdate={update}
        />
      )}
    </main>
  );
}
